import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { agreementsApi } from '../utils/api';
import AgentVerdictBadge from '../components/AgentVerdictBadge';

// PRD-011 WU-7 — Completion Attestation page.
//
// The "system of record's receipt" rendered as a print-friendly,
// single-column artifact. Five sections:
//   1. Parties (seller + buyer signing data)
//   2. Outcomes delivered (sell config summary + spec version)
//   3. Evidence (verdict + source + hash + rationale, from the
//      attestation's frozen snapshot — not refetched live)
//   4. Money (Agreement value / Token Broker fee / Net to seller)
//   5. Footer (full content hash, generated_at, append-only notice)
//
// All data comes from the frozen attestation snapshot. No agreement
// detail call is needed.

function fmtDate(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  return d.toLocaleDateString('en-US', {
    year: 'numeric', month: 'long', day: 'numeric',
  });
}

function fmtDateTime(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  return d.toISOString().replace('T', ' ').slice(0, 16) + ' UTC';
}

function shortHash(hex) {
  if (!hex || hex.length < 20) return hex || '—';
  return `${hex.slice(0, 8)}…${hex.slice(-8)}`;
}

function formatMoney(cents, currencyCode) {
  const decimal = (cents / 100).toFixed(2);
  const [whole, frac] = decimal.split('.');
  const wholeWithCommas = whole.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  return `${currencyCode} ${wholeWithCommas}.${frac}`;
}

export default function CompletionAttestation() {
  const { id } = useParams();
  const [attestation, setAttestation] = useState(null);
  const [moneyBlock, setMoneyBlock] = useState(null);
  const [agreement, setAgreement] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let mounted = true;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const [attRes, agRes] = await Promise.all([
          agreementsApi.getAttestation(id),
          agreementsApi.get(id),
        ]);
        if (!mounted) return;
        setAttestation(attRes.data.attestation);
        setMoneyBlock(attRes.data.moneyBlock);
        setAgreement(agRes.data.agreement || agRes.data);
      } catch (e) {
        if (!mounted) return;
        setError(
          e?.response?.data?.error ||
          e?.message ||
          'Failed to load attestation'
        );
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, [id]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
      </div>
    );
  }

  if (error || !attestation) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-6">
        <div className="bg-white border border-gray-200 rounded-lg p-6 max-w-md">
          <h1 className="text-lg font-semibold text-gray-900 mb-2">
            Attestation not available
          </h1>
          <p className="text-sm text-gray-600 mb-4">
            {error || 'An attestation has not been generated for this agreement yet.'}
          </p>
          <Link to={`/agreements/${id}`} className="text-sm text-primary-600 hover:text-primary-700">
            ← Back to agreement
          </Link>
        </div>
      </div>
    );
  }

  const parties = attestation.partiesSnapshot || {};
  const seller = parties.seller || {};
  const buyer = parties.buyer || {};
  const evidence = attestation.evidenceRefsSnapshot || [];
  const specs = attestation.specVersionsSnapshot || [];

  // Group evidence by deliverable. The snapshot is ordered server-side by
  // (deliverable_id, fetched_at) so the group order is stable.
  const evidenceByDeliverable = evidence.reduce((acc, e) => {
    if (!acc[e.deliverable_id]) acc[e.deliverable_id] = [];
    acc[e.deliverable_id].push(e);
    return acc;
  }, {});

  const specByDeliverable = specs.reduce((acc, s) => {
    acc[s.deliverable_id] = s;
    return acc;
  }, {});

  // Outcomes structure comes from the live agreement read; the attestation
  // doesn't snapshot Outcome names because they're already locked on
  // agreement_outcomes (immutable post-signing) so reading them live is
  // safe and avoids duplicating data.
  const outcomes = (agreement?.outcomes) || [];

  return (
    <div className="min-h-screen bg-gray-100 py-6 print:bg-white print:py-0">
      <style>{`
        @media print {
          .no-print { display: none !important; }
          body { background: white !important; }
          .attestation-page {
            max-width: none !important;
            margin: 0 !important;
            box-shadow: none !important;
            border: none !important;
          }
          .evidence-row {
            break-inside: avoid;
          }
        }
      `}</style>

      <div className="max-w-3xl mx-auto px-4 mb-4 no-print flex items-center justify-between">
        <Link to={`/agreements/${id}`} className="text-sm text-primary-600 hover:text-primary-700">
          ← Back to agreement
        </Link>
        <button
          type="button"
          onClick={() => window.print()}
          className="text-sm px-3 py-1.5 rounded border border-gray-300 text-gray-700 bg-white hover:bg-gray-50"
        >
          Print / Save PDF
        </button>
      </div>

      <article
        className="attestation-page max-w-3xl mx-auto bg-white border border-gray-200 rounded-lg shadow-sm p-10 print:p-0"
        style={{ fontFamily: 'Georgia, "Times New Roman", serif' }}
      >
        {/* Header band */}
        <header className="border-b-2 border-gray-900 pb-5 mb-7">
          <p className="text-xs uppercase tracking-[0.2em] text-gray-600 mb-2">
            Completion Attestation
          </p>
          <h1 className="text-2xl font-bold text-gray-900 leading-tight">
            {agreement?.name || 'Agreement'}
          </h1>
          <p className="text-sm text-gray-600 mt-2">
            Issued {fmtDateTime(attestation.generatedAt)} ·{' '}
            <span className="font-mono text-xs">Ref {shortHash(attestation.contentHashHex)}</span>
          </p>
        </header>

        {/* Section 1 — Parties */}
        <section className="mb-7">
          <h2 className="text-xs uppercase tracking-widest text-gray-600 mb-3 font-sans">
            Parties
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <PartyBlock label="Agency" party={seller} />
            <PartyBlock label="Client" party={buyer} />
          </div>
        </section>

        {/* Section 2 — Outcomes delivered */}
        <section className="mb-7">
          <h2 className="text-xs uppercase tracking-widest text-gray-600 mb-3 font-sans">
            Outcomes delivered
          </h2>
          {outcomes.length === 0 ? (
            <p className="text-sm text-gray-600 italic">No outcomes recorded.</p>
          ) : (
            <ul className="space-y-4">
              {outcomes.map((o) => {
                const sellSummary = (o.sell_price_per_unit != null && o.sell_quantity != null)
                  ? `${o.sell_quantity} × ${formatMoney(Math.round(parseFloat(o.sell_price_per_unit) * 100), attestation.currencyCode)} ${o.sell_unit_name ? ` per ${o.sell_unit_name}` : ''} = ${formatMoney(Math.round(parseFloat(o.sell_price_per_unit) * parseFloat(o.sell_quantity) * 100), attestation.currencyCode)}`
                  : null;
                const delivs = o.deliverables || [];
                return (
                  <li key={o.id} className="border-l-2 border-gray-200 pl-4">
                    <p className="text-base font-semibold text-gray-900">{o.name}</p>
                    {sellSummary && (
                      <p className="text-sm text-gray-700 mt-1 font-mono">{sellSummary}</p>
                    )}
                    {delivs.map((d) => {
                      const spec = specByDeliverable[d.id];
                      return (
                        <p key={d.id} className="text-xs text-gray-600 mt-1">
                          Deliverable <span className="font-medium text-gray-700">{d.name}</span>
                          {spec && (
                            <>
                              {' '}— spec v{spec.spec_version}, locked{' '}
                              {fmtDate(spec.locked_at)}
                            </>
                          )}
                        </p>
                      );
                    })}
                  </li>
                );
              })}
            </ul>
          )}
        </section>

        {/* Section 3 — Evidence */}
        <section className="mb-7">
          <h2 className="text-xs uppercase tracking-widest text-gray-600 mb-3 font-sans">
            Evidence
          </h2>
          {evidence.length === 0 ? (
            <p className="text-sm text-gray-600 italic">No evidence captured.</p>
          ) : (
            <div className="space-y-4">
              {Object.entries(evidenceByDeliverable).map(([deliverableId, rows]) => (
                <div key={deliverableId}>
                  <p className="text-xs text-gray-600 mb-2">
                    Deliverable{' '}
                    <span className="font-mono">{deliverableId.slice(0, 8)}</span>
                  </p>
                  <ul className="space-y-2">
                    {rows.map((e) => (
                      <li
                        key={e.id}
                        className="evidence-row border border-gray-200 rounded-md p-3 bg-gray-50"
                      >
                        <div className="flex items-start justify-between gap-3 mb-1">
                          <div className="flex items-center gap-2 flex-wrap">
                            <AgentVerdictBadge verdict={e.verdict} />
                            <span className="text-xs text-gray-600">
                              {e.source_system}
                            </span>
                          </div>
                          <span className="text-xs text-gray-600 font-mono whitespace-nowrap">
                            {fmtDateTime(e.fetched_at)}
                          </span>
                        </div>
                        {e.rationale_first_120_chars && (
                          <p className="text-xs text-gray-700 mt-2 leading-relaxed">
                            {e.rationale_first_120_chars}
                            {e.rationale_first_120_chars.length === 120 ? '…' : ''}
                          </p>
                        )}
                        <div className="text-[11px] text-gray-600 mt-2 flex items-center gap-3 flex-wrap font-mono">
                          {e.content_hash && (
                            <span>hash {shortHash(e.content_hash)}</span>
                          )}
                          {e.external_url && (
                            <a
                              href={e.external_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-primary-600 hover:text-primary-700"
                            >
                              source ↗
                            </a>
                          )}
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          )}
        </section>

        {/* Section 4 — Money */}
        <section className="mb-7">
          <h2 className="text-xs uppercase tracking-widest text-gray-600 mb-3 font-sans">
            Money
          </h2>
          <div className="border border-gray-300 rounded-md p-5 bg-white">
            <MoneyRow
              label="Agreement value"
              value={moneyBlock?.agreementValue}
            />
            <MoneyRow
              label={`Verdict fee (${(moneyBlock?.platformFeePct ?? 5).toFixed(1)}%)`}
              value={moneyBlock?.platformFee}
            />
            <MoneyRow
              label={`Net to ${seller.legal_name || seller.name || "agency"}`}
              value={moneyBlock?.netToSeller}
              bold
            />
          </div>
        </section>

        {/* Section 5 — Footer */}
        <footer className="border-t border-gray-300 pt-5 mt-7">
          <p className="text-[11px] text-gray-600 mb-1">Content hash (SHA-256)</p>
          <p className="font-mono text-xs text-gray-800 break-all mb-3">
            {attestation.contentHashHex}
          </p>
          <p className="text-xs text-gray-600">
            Generated {fmtDateTime(attestation.generatedAt)}.
            {' '}This record is append-only and cannot be modified after issuance.
          </p>
        </footer>
      </article>
    </div>
  );
}

function PartyBlock({ label, party }) {
  return (
    <div>
      <p className="text-xs uppercase tracking-wider text-gray-600 mb-1 font-sans">
        {label}
      </p>
      <p className="text-base font-bold text-gray-900">{party.name || '—'}</p>
      {(party.signing_member_name || party.signing_member_email) && (
        <p className="text-sm text-gray-700 mt-1">
          Signed by{' '}
          <span className="font-medium">
            {party.signing_member_name || party.signing_member_email}
          </span>
        </p>
      )}
      {party.signed_at && (
        <p className="text-xs text-gray-600 mt-0.5 font-mono">
          {fmtDateTime(party.signed_at)}
        </p>
      )}
    </div>
  );
}

function MoneyRow({ label, value, bold = false }) {
  return (
    <div className="flex items-baseline justify-between py-1.5 border-b border-gray-100 last:border-b-0">
      <span className={`text-sm ${bold ? 'font-semibold text-gray-900' : 'text-gray-700'}`}>
        {label}
      </span>
      <span
        className={`font-mono text-base text-right ${bold ? 'font-bold text-gray-900' : 'text-gray-800'}`}
      >
        {value || '—'}
      </span>
    </div>
  );
}
